import type {
  CommunityRolePreview,
  PostSearchResult,
  ThreadSearchResult,
} from "@outverse-video/outverse-api-client-lib";
import { derived, writable } from "svelte/store";
import { processRoleLabel } from "app/utils/strings";
import MemberProfilePanel from "$components/member/Members/MemberProfilePanel/MemberProfilePanel.svelte";
import { sidePanel } from "app/stores/sidePanel";
import { apiReady } from "./auth";

interface Loading {
  memberFetched: boolean;
  searchFetched: boolean;
}

type MemberProfilePanelType = Loading & {
  uuid: string;
  profileColor: string;
  profileImage: string;
  handle: string;
  headline: string;
  bio: string;
  instagram: string;
  twitter: string;
  linkedin: string;
  github: string;
  website: string;
  threadsCount?: number;
  repliesCount?: number;
  threads?: ThreadSearchResult[];
  replies?: PostSearchResult[];
  joinedCommunityAt: string;
  roles: CommunityRolePreview[] | null;
};

const memberProfilePanelStore = () => {
  const initialState: MemberProfilePanelType = {
    uuid: "",
    profileColor: "",
    profileImage: "",
    handle: "",
    headline: "",
    bio: "",
    instagram: "",
    twitter: "",
    linkedin: "",
    github: "",
    website: "",
    threadsCount: 0,
    repliesCount: 0,
    threads: [],
    replies: [],
    joinedCommunityAt: "",
    roles: [],
    memberFetched: false,
    searchFetched: false,
  };

  const { subscribe, update, set } = writable<MemberProfilePanelType>({
    ...initialState,
  });
  return {
    subscribe,
    clear: () => set({ ...initialState }),
    hide: () => update((state) => ({ ...state, visible: false })),
    clearAndShow: async (uuid: string, communitySlug: string | undefined) => {
      // if there is no communitySlug, there is no need to fetch the data
      if (!communitySlug) return;

      update((state) => {
        if (state.uuid !== uuid) {
          return { ...initialState };
        }
        return state;
      });

      sidePanel.setPanel(MemberProfilePanel, "Member");
      sidePanel.show();

      const api = await apiReady;
      // fetch member data
      const data = (await api.communitiesMembersRetrieve({ uuid, communitySlug })).data;

      // wait for 300ms to show the loading state
      setTimeout(() => {
        update((state) => ({
          ...state,
          ...data,
          joinedCommunityAt: data.joinedCommunityAt ?? "",
          roles: (data.roles || []).map((role) => {
            const name = role.name || "";
            return {
              ...role,
              name: processRoleLabel(name),
            };
          }),
          memberFetched: true,
        }));
      }, 300);

      // fetch search data of the member
      const responses = await Promise.all([
        api.searchPostsList({ author: uuid, communitySlug }),
        api.searchThreadsList({ author: uuid, communitySlug }),
      ]);
      update((state) => ({
        ...state,
        repliesCount: responses[0].data.count,
        replies: responses[0].data.results,
        threadsCount: responses[1].data.count,
        threads: responses[1].data.results,
        searchFetched: true,
      }));
    },
  };
};

export const memberProfilePanel = memberProfilePanelStore();

export const isMemberFetched = derived(
  memberProfilePanel,
  ($memberProfilePanel) => $memberProfilePanel.memberFetched
);
export const isSearchFetched = derived(
  memberProfilePanel,
  ($memberProfilePanel) => $memberProfilePanel.searchFetched
);
export const selectForumsActiveIn = derived(memberProfilePanel, ($memberProfilePanel) => {
  if (!$memberProfilePanel.threads && !$memberProfilePanel.replies) return [];
  const forumsFromThreads = $memberProfilePanel.threads?.map((thread) => thread.forumSpace);
  const forumsFromReplies = $memberProfilePanel.replies?.map((reply) => reply.thread.forumSpace);
  // remove duplicates based on the forum title and return the array of unique forums
  return [
    ...new Map(
      [...(forumsFromThreads ?? []), ...(forumsFromReplies ?? [])].map((item) => [
        item["title"],
        item,
      ])
    ).values(),
  ];
});
export const selectRecentActivity = derived(memberProfilePanel, ($memberProfilePanel) => {
  if (!$memberProfilePanel.threads && $memberProfilePanel.replies)
    return $memberProfilePanel.replies;
  if ($memberProfilePanel.threads && !$memberProfilePanel.replies)
    return $memberProfilePanel.threads;
  if (!$memberProfilePanel.threads && !$memberProfilePanel.replies) return [];

  const recentActivity: (ThreadSearchResult | PostSearchResult)[] = [
    ...($memberProfilePanel.threads ?? []),
    ...($memberProfilePanel.replies ?? []),
  ].sort((a, b) => {
    return new Date(b.created).getTime() - new Date(a.created).getTime();
  });
  return recentActivity;
});
